import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import SavingStates from "../constants/states/SavingStates";
import {FormFieldWithValidityState} from "./FormFieldWithValidityState";
import LoadingStates from "../constants/states/LoadingStates";


class SpaceEditViewState {
    constructor(
        savingState,
        isLimitedEdit,
        loadingState,
        spaceId,
        title,
        description,
        price,
        minimumDays,
        spaceAddressGooglePlaceId,
        spaceAddressLatitude,
        spaceAddressLongitude,
        spaceAddress,
        imageNames
    ) {
        this.savingState = savingState;
        this.isLimitedEdit = isLimitedEdit;
        this.loadingState = loadingState;
        this.spaceId = spaceId;
        this.title = title;
        this.description = description;
        this.price = price;
        this.minimumDays = minimumDays;
        this.spaceAddressGooglePlaceId = spaceAddressGooglePlaceId;
        this.spaceAddressLatitude = spaceAddressLatitude;
        this.spaceAddressLongitude = spaceAddressLongitude;
        this.spaceAddress = spaceAddress;
        this.imageNames = imageNames;
    }

    equals(other) {
        return this.savingState === other.savingState &&
            this.isLimitedEdit === other.isLimitedEdit &&
            this.spaceId === other.spaceId &&
            this.loadingState === other.loadingState &&
            this.title.equals(other.title) &&
            this.description.equals(other.description) &&
            this.price.equals(other.price) &&
            this.minimumDays.equals(other.minimumDays) &&
            this.spaceAddressGooglePlaceId.equals(other.spaceAddressGooglePlaceId) &&
            this.spaceAddressLatitude === other.spaceAddressLatitude &&
            this.spaceAddressLongitude === other.spaceAddressLongitude &&
            this.spaceAddress === other.spaceAddress &&
            this.imageNames.equals(other.imageNames);
    }
}

class ImmutableSpaceEditViewStateBuilder {

    constructor() {
        this.savingState = SavingStates.NOT_STARTED;
        this.loadingState = LoadingStates.INITIALIZED;
        this.spaceId = 0;
        this.isLimitedEdit = false;
        this.title = new FormFieldWithValidityState("", false, false);;
        this.description = new FormFieldWithValidityState("", false, false);
        this.price = new FormFieldWithValidityState("", false, false);
        this.minimumDays = new FormFieldWithValidityState(1, true, false);
        this.spaceAddressGooglePlaceId = FormFieldWithValidityState.initialDefaults("");
        this.spaceAddressLatitude = 0;
        this.spaceAddressLongitude = 0;
        this.spaceAddress = "";
        this.imageNames = new FormFieldWithValidityState([], true, false);
    }

    static fromState(state) {
        return new ImmutableSpaceEditViewStateBuilder()
            .setSavingState(state.savingState)
            .setIsLimitedEdit(state.isLimitedEdit)
            .setLoadingState(state.loadingState)
            .setSpaceId(state.spaceId)
            .setTitle(state.title.currentValue, state.title.isValid, state.title.isTouched)
            .setDescription(state.description.currentValue, state.description.isValid, state.description.isTouched)
            .setPrice(state.price.currentValue, state.price.isValid, state.price.isTouched)
            .setMinimumDays(state.minimumDays.currentValue, state.minimumDays.isValid, state.minimumDays.isTouched)
            .setSpaceAddressGooglePlaceId(
                state.spaceAddressGooglePlaceId.currentValue,
                state.spaceAddressGooglePlaceId.isValid,
                state.spaceAddressGooglePlaceId.isTouched)
            .setSpaceAddressLatitude(state.spaceAddressLatitude)
            .setSpaceAddressLongitude(state.spaceAddressLongitude)
            .setSpaceAddress(state.spaceAddress)
            .setImageNames(state.imageNames.currentValue, state.imageNames.isValid, state.imageNames.isTouched);
    }

    setSavingState(newState) {
        this.savingState = newState;
        return this;
    }

    setTitle(title, isValid, isTouched) {
        this.title = new FormFieldWithValidityState(title, isValid, isTouched);
        return this;
    }

    setDescription(description, isValid, isTouched) {
        this.description = new FormFieldWithValidityState(description, isValid, isTouched);
        return this;
    }

    setPrice(price, isValid, isTouched) {
        this.price = new FormFieldWithValidityState(price, isValid, isTouched);
        return this;
    }

    setMinimumDays(minimumDays, isValid, isTouched) {
        this.minimumDays = new FormFieldWithValidityState(minimumDays, isValid, isTouched);
        return this;
    }

    setSpaceAddressGooglePlaceId(
        spaceAddressGooglePlaceId,
        isValid,
        isTouched)
    {
        this.spaceAddressGooglePlaceId = new FormFieldWithValidityState(spaceAddressGooglePlaceId, isValid, isTouched);
        return this;
    }

    setSpaceAddressLatitude(spaceAddressLatitude)
    {
        this.spaceAddressLatitude = spaceAddressLatitude;
        return this;
    }

    setSpaceAddressLongitude(spaceAddressLongitude)
    {
        this.spaceAddressLongitude = spaceAddressLongitude;
        return this;
    }

    setSpaceAddress(spaceAddress)
    {
        this.spaceAddress = spaceAddress;
        return this;
    }

    resetSpaceAddress() {
        this.spaceAddressGooglePlaceId = FormFieldWithValidityState.initialDefaults("");
        this.spaceAddressLatitude = 0;
        this.spaceAddressLongitude = 0;
        this.spaceAddress = "";
        return this;
    }

    setIsLimitedEdit(isLimitedEdit) {
        this.isLimitedEdit = isLimitedEdit;
        return this;
    }

    setSpaceId(spaceId) {
        this.spaceId = spaceId;
        return this;
    }

    setLoadingState(loadingState) {
        this.loadingState = loadingState;
        return this;
    }

    setImageNames(imageNames, isValid, isTouched) {
        this.imageNames = new FormFieldWithValidityState(imageNames, isValid, isTouched);;
        return this;
    }

    build() {
        return Object.freeze(
            new SpaceEditViewState(
                this.savingState,
                this.isLimitedEdit,
                this.loadingState,
                this.spaceId,
                this.title,
                this.description,
                this.price,
                this.minimumDays,
                this.spaceAddressGooglePlaceId,
                this.spaceAddressLatitude,
                this.spaceAddressLongitude,
                this.spaceAddress,
                this.imageNames));
    }
}


class SpaceEditViewStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.SPACE_EDIT_VIEW_STORE_UPDATED, ImmutableSpaceEditViewStateBuilder);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_TITLE_CHANGED, this.handleTitleChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_DESCRIPTION_CHANGED, this.handleDescriptionChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_PRICE_CHANGED, this.handlePriceChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_MINIMUM_DAYS_CHANGED, this.handleMinimumDaysChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_ADDRESS_AUTOCOMPLETE_CHANGED, this.handleGoogleAutocompleteChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_ADDRESS_AUTOCOMPLETE_CLEARED, this.handleGoogleAutocompleteCleared);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_SAVE_SUBMITTED, this.handleSaveStarted);
        this.subscribeToEvent(ActionResultEvents.PRIVATE_SPACE_LOADED, this.handleLoadSpace);
        this.subscribeToEvent(ActionResultEvents.PRIVATE_SPACE_NOT_FOUND, this.handleSpaceNotFound);
        this.subscribeToEvent(ActionResultEvents.SPACE_EDITED, this.resetStore);
        this.subscribeToEvent(UIInteractionEvents.SPACE_EDIT_SET_SPACE_ID, this.handleSetSpaceId);
        this.subscribeToEvent(UIInteractionEvents.DELETE_IMAGE, this.handleImageDirtyState);
        this.subscribeToEvent(ActionResultEvents.IMAGE_UPLOAD_FINISHED, this.handleImageDirtyState);
    }

    handleImageDirtyState = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setImageNames(this.state.imageNames.currentValue, true, true)
            .build();
    };

    handleSetSpaceId = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setSpaceId(data.id)
            .setLoadingState(LoadingStates.LOADING)
            .build();
    };

    handleSpaceNotFound = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setLoadingState(LoadingStates.FAILED_TO_LOAD)
            .build();
    };

    handleLoadSpace = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setLoadingState(LoadingStates.FINISHED_LOADING)
            .setSpaceId(data.space.spaceId)
            .setTitle(data.space.title, true, false)
            .setDescription(data.space.description, true, false)
            .setPrice(data.space.pricePerDay, true, false)
            .setMinimumDays(data.space.minimumDays, true, false)
            .setSpaceAddressGooglePlaceId(data.space.googlePlaceId, true, false)
            .setSpaceAddressLatitude(data.space.latitude)
            .setSpaceAddressLongitude(data.space.longitude)
            .setSpaceAddress(data.space.formattedAddress)
            .setIsLimitedEdit(data.isLimitedEdit)
            .setImageNames(data.space.imageNames, true, false)
            .build();
    };

    handleSaveStarted = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.SAVING)
            .build();
    };

    handleTitleChanged = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setTitle(data.title, data.isValid, true)
            .build();
    };

    handleDescriptionChanged = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setDescription(data.description, data.isValid, true)
            .build();
    };

    handlePriceChanged = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setPrice(data.price, data.isValid, true)
            .build();
    };

    handleMinimumDaysChanged = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setMinimumDays(data.minimumDays, data.isValid, true)
            .build();
    };

    handleGoogleAutocompleteChanged = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .setSpaceAddressGooglePlaceId(
                data.placeId,
                data.isValid,
                true)
            .setSpaceAddressLatitude(data.latitude)
            .setSpaceAddressLongitude(data.longitude)
            .setSpaceAddress(data.address)
            .build();
    };

    handleGoogleAutocompleteCleared = (topic, data) => {
        return ImmutableSpaceEditViewStateBuilder.fromState(this.state)
            .resetSpaceAddress()
            .build()
    }



}

export default new SpaceEditViewStore();